import { useAtomValue } from "jotai";
import React, { Suspense, useCallback, useEffect, useRef } from "react";
import { fetchQuery, useQueryLoader, useRelayEnvironment } from "react-relay";
import { Subscription } from "relay-runtime";

import { Loader } from "@whoosh/design-system/src/components/display";

import { sessionQueryVersionAtom } from "../../features/Session";
import {
  SessionCheckerQueryHandler,
  SessionCheckerQueryHandlerQueryString,
} from "./SessionCheckerQueryHandler";
import {
  SessionRefreshProvider,
  type SessionRefreshCallbacks,
} from "./SessionRefreshContext";
import { SessionCheckerQueryHandlerQuery } from "./__generated__/SessionCheckerQueryHandlerQuery.graphql";

export const SessionCheckerQueryWrapper = () => {
  const sessionQueryVersion = useAtomValue(sessionQueryVersionAtom);
  const loadedSessionQueryVersionRef = useRef<number | null>(null);
  const [queryRef, loadQuery, disposeQuery] =
    useQueryLoader<SessionCheckerQueryHandlerQuery>(
      SessionCheckerQueryHandlerQueryString
    );
  const environment = useRelayEnvironment();
  const refreshSubscriptionRef = useRef<Subscription | null>(null);

  useEffect(() => {
    refreshSubscriptionRef.current?.unsubscribe();
    refreshSubscriptionRef.current = null;
    loadedSessionQueryVersionRef.current = sessionQueryVersion;
    disposeQuery();
    loadQuery({}, { fetchPolicy: "network-only" });
  }, [disposeQuery, loadQuery, sessionQueryVersion]);

  useEffect(
    () => () => {
      refreshSubscriptionRef.current?.unsubscribe();
    },
    []
  );

  const requestSessionRefresh = useCallback(
    ({ onCompleted, onError }: SessionRefreshCallbacks) => {
      refreshSubscriptionRef.current?.unsubscribe();

      let hasSettled = false;
      const subscription = fetchQuery(
        environment,
        SessionCheckerQueryHandlerQueryString,
        {},
        { fetchPolicy: "network-only" }
      ).subscribe({
        complete: () => {
          hasSettled = true;
          refreshSubscriptionRef.current = null;
          onCompleted();
        },
        error: () => {
          hasSettled = true;
          refreshSubscriptionRef.current = null;
          onError();
        },
      });

      if (!hasSettled) {
        refreshSubscriptionRef.current = subscription;
      }
    },
    [environment]
  );

  return (
    <SessionRefreshProvider value={requestSessionRefresh}>
      <Suspense fallback={<Loader />}>
        {queryRef && loadedSessionQueryVersionRef.current !== null ? (
          <SessionCheckerQueryHandler
            queryVersion={loadedSessionQueryVersionRef.current}
            sessionRef={queryRef}
          />
        ) : null}
      </Suspense>
    </SessionRefreshProvider>
  );
};
